import React from 'react';

const ListingStats = ({ listing }) => {
  const getStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-800/50', label: 'Active' },
      inactive: { bg: 'bg-gray-700', text: 'text-gray-300', border: 'border-gray-600', label: 'Inactive' },
      sold: { bg: 'bg-blue-900/30', text: 'text-blue-400', border: 'border-blue-800/50', label: 'Sold' },
      pending: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-800/50', label: 'Pending' }
    };
    
    const cfg = config[status] || config.inactive;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
        {cfg.label}
      </span>
    );
  };

  const getModerationBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-800/50', label: 'Pending Review' },
      approved: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-800/50', label: 'Approved' },
      rejected: { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-800/50', label: 'Rejected' },
      flagged: { bg: 'bg-[#ff660e]/20', text: 'text-[#ff660e]', border: 'border-[#ff660e]/30', label: 'Flagged' }
    };
    
    const cfg = config[status] || config.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
        {cfg.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const adminData = listing?.admin_data || {};

  return (
    <div className="bg-gray-800 rounded-2xl shadow-lg border border-gray-700 p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <span className="text-2xl text-[#ff660e]">📊</span>
          <span>Listing Statistics</span>
        </h3>
        <div className="text-sm text-gray-400 bg-gray-700 px-3 py-1 rounded-lg border border-gray-600">
          ID: <span className="font-mono font-semibold text-gray-300">{listing?.listing_id}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-br from-gray-750 to-gray-800 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors group">
          <div className="text-2xl font-bold text-white mb-1 group-hover:text-[#ff660e] transition-colors">
            {listing?.view_count || 0}
          </div>
          <div className="text-sm text-gray-400 font-medium">Total Views</div>
        </div>
        
        <div className="bg-gradient-to-br from-gray-750 to-gray-800 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors group">
          <div className="text-2xl font-bold text-white mb-1 group-hover:text-[#ff660e] transition-colors">
            {listing?.images?.length || 0}
          </div>
          <div className="text-sm text-gray-400 font-medium">Images</div>
        </div>
      </div>

      {/* Status Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between bg-gray-750 rounded-xl p-4 border border-gray-700">
          <span className="text-gray-300 font-medium">Status</span>
          {getStatusBadge(listing?.status)}
        </div>
        
        <div className="flex items-center justify-between bg-gray-750 rounded-xl p-4 border border-gray-700">
          <span className="text-gray-300 font-medium">Moderation</span>
          {getModerationBadge(listing?.moderation_status)}
        </div>

        {/* Feature Status */}
        <div className="bg-gray-750 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-white">Featured</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${listing?.is_featured ? 'bg-purple-900/30 text-purple-400 border-purple-800/50' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
              {listing?.is_featured ? '⭐ Featured' : 'Not Featured'}
            </span>
          </div>
          {listing?.featured_until && (
            <div className="text-sm text-gray-400">
              Featured until: {new Date(listing.featured_until).toLocaleDateString()}
            </div>
          )}
        </div>

        {/* Boost Status */}
        <div className="bg-gray-750 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-white group-hover:text-[#ff660e] transition-colors">Boost Status</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${adminData.is_boosted ? 'bg-[#ff660e]/20 text-[#ff660e] border-[#ff660e]/30' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
              {adminData.is_boosted ? '🚀 Boosted' : 'Not Boosted'}
            </span>
          </div>
          {adminData.boost_history?.length > 0 && (
            <div className="text-sm text-gray-400">
              {adminData.boost_history.length} boost(s) used
            </div>
          )}
        </div>

        {/* Order Stats */}
        <div className="bg-blue-900/20 rounded-xl p-4 border border-blue-800/30 hover:border-blue-700/50 transition-colors">
          <h4 className="font-medium text-white mb-2">Order Statistics</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="group">
              <div className="text-gray-400 group-hover:text-gray-300 transition-colors">Total Orders</div>
              <div className="font-bold text-white group-hover:text-[#ff660e] transition-colors">
                {adminData.order_stats?.total_orders || 0}
              </div>
            </div>
            <div className="group">
              <div className="text-gray-400 group-hover:text-gray-300 transition-colors">Revenue</div>
              <div className="font-bold text-white group-hover:text-[#ff660e] transition-colors">
                {formatCurrency(adminData.order_stats?.total_revenue || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Report Status */}
        <div className="bg-gray-750 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-white">Reports</div>
              <div className="text-sm text-gray-400">
                {adminData.report_history?.length || 0} report(s)
              </div>
            </div>
            {adminData.has_active_reports && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-900/30 text-red-400 border border-red-800/50 animate-pulse">
                ⚠️ Needs Review
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingStats;